import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

const PhoneShowcase = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('phone-showcase');
      if (element) {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Calculate how much of the element is visible
        // When element enters viewport: progress = 0
        // When element is centered: progress = 0.5
        // When element exits viewport: progress = 1
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = windowHeight / 2;
        const distance = elementCenter - viewportCenter;
        const maxDistance = windowHeight / 2 + rect.height / 2;
        
        // Normalize to 0-1 range, where 0.5 is centered
        const progress = Math.max(0, Math.min(1, 0.5 - (distance / maxDistance / 2)));
        
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width; // 0 to 1
    const y = (e.clientY - rect.top) / rect.height; // 0 to 1
    setMousePosition({ x, y });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePosition({ x: 0.5, y: 0.5 });
  };

  // Smooth animations based on scroll
  // Phone tilts slightly and scales up when in view
  const scrollTiltY = (scrollProgress - 0.5) * 10; // -5 to +5 degrees
  const scrollTiltX = Math.sin(scrollProgress * Math.PI) * 5; // Subtle X tilt
  const scale = 0.85 + (Math.sin(scrollProgress * Math.PI) * 0.15); // 0.85 to 1.0
  const translateY = (1 - scrollProgress) * 30; // Parallax effect

  // Mouse hover 3D tilt effect
  const mouseTiltY = isHovered ? (mousePosition.x - 0.5) * 30 : 0; // -15 to +15 degrees
  const mouseTiltX = isHovered ? (mousePosition.y - 0.5) * -30 : 0; // -15 to +15 degrees
  const hoverScale = isHovered ? 1.05 : 1;

  // Combine scroll and mouse effects
  const finalTiltY = scrollTiltY + mouseTiltY;
  const finalTiltX = scrollTiltX + mouseTiltX;
  const finalScale = scale * hoverScale;

  return (
    <section id="phone-showcase" className="py-32 bg-slate-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-1/2 left-1/2 w-[800px] h-[800px] bg-purple-500/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-pink-500/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm border border-purple-500/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">RUYAN Labs</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                Kendi ürünlerimizi de geliştiriyoruz
              </span>
            </h2>

            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Müşterilerimiz için çözümler üretirken, kendi ürünlerimizi de hayata geçiriyoruz.
              RUYAN Labs bünyesinde geliştirdiğimiz yapay zekâ destekli uygulamalar,
              bizim de teknolojiye olan inancımızın ve yeteneklerimizin kanıtı.
            </p>

            {/* Vision Points */}
            <div className="space-y-4">
              {[
                'Sadece müşteriler için değil, kendimiz için de üretiyoruz',
                'AI ve kullanıcı davranışını gerçek ürünlerde test ediyoruz',
                'Öğrendiklerimizi müşteri projelerine aktarıyoruz',
                'Teknolojiye sadece tüketici değil, üretici olarak bakıyoruz'
              ].map((point, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mt-2 flex-shrink-0"></div>
                  <span className="text-gray-300">{point}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              <p className="text-gray-400 text-sm">
                <span className="text-purple-400 font-semibold">Örnek:</span> RUYAN Labs'te geliştirdiğimiz
                rüya yorumlama uygulaması, yapay zekâ entegrasyonu ve kullanıcı deneyimi konusundaki
                uzmanlığımızın bir yansıması.
              </p>
            </div>
          </div>

          {/* Right Phone Mockup */}
          <div 
            className="relative flex items-center justify-center"
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div 
              className="phone-mockup-container"
              style={{
                transform: `perspective(1200px) rotateY(${finalTiltY}deg) rotateX(${finalTiltX}deg) scale(${finalScale}) translateY(${translateY}px)`,
                transition: isHovered 
                  ? 'transform 0.1s cubic-bezier(0.4, 0, 0.2, 1)' 
                  : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {/* Phone Frame */}
              <div className="phone-frame">
                {/* Notch */}
                <div className="phone-notch"></div>
                
                {/* Screen Content - PNG Image */}
                <div className="phone-screen">
                  <img 
                    src="/images/dream-app-screenshot3x.png" 
                    alt="Rüya Yorumlama Uygulaması"
                    className="w-full h-full object-cover rounded-[42px]"
                    onError={(e) => {
                      // Fallback to original content if image not found
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  
                  {/* Fallback Content (original design) - Hidden by default */}
                  <div className="fallback-content" style={{ display: 'none' }}>
                    {/* Status Bar */}
                    <div className="flex justify-between items-center px-6 py-3 text-white text-xs">
                      <span>9:41</span>
                      <div className="flex space-x-1">
                        <div className="w-4 h-4">📶</div>
                        <div className="w-4 h-4">📶</div>
                        <div className="w-4 h-4">🔋</div>
                      </div>
                    </div>

                    {/* App Content */}
                    <div className="px-6 py-4 flex-1">
                      <h3 className="text-2xl font-bold text-white mb-2">Rüyalarım</h3>
                      <p className="text-gray-400 text-sm mb-6">Bilinçaltının kapılarını aç</p>

                      {/* Dream Cards */}
                      <div className="space-y-4">
                        {[
                          { title: 'Uçuş Rüyası', emoji: '🦋', time: '2 saat önce' },
                          { title: 'Okyanus Yolculuğu', emoji: '🌊', time: '1 gün önce' },
                          { title: 'Eski Ev', emoji: '🏠', time: '3 gün önce' },
                        ].map((dream, index) => (
                          <div 
                            key={index} 
                            className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-4 border border-slate-700/50"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="text-3xl">{dream.emoji}</div>
                              <div className="flex-1">
                                <div className="text-white font-medium">{dream.title}</div>
                                <div className="text-gray-500 text-xs">{dream.time}</div>
                              </div>
                              <div className="text-purple-400">→</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className="flex justify-around items-center px-6 py-4 border-t border-slate-700/50">
                      <div className="text-purple-400 text-xl">🏠</div>
                      <div className="text-gray-500 text-xl">📖</div>
                      <div className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-2xl shadow-lg shadow-purple-500/50">+</div>
                      <div className="text-gray-500 text-xl">📊</div>
                      <div className="text-gray-500 text-xl">👤</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              <div className="phone-glow"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhoneShowcase;